import {notifyRoamingSettingsChanged} from './roamingSettingsSync';

export const LOXO_ROAMING_KEYS = {
  emailSyncOnSend: 'emailSyncOnSend',
  podUrl: 'podUrl',
  refreshToken: 'refreshToken',
  jwt: 'jwt',
} as const;

export const getRoamingSettings = (): Office.RoamingSettings | null => {
  if (
    typeof Office === 'undefined' ||
    Office.context?.roamingSettings == null
  ) {
    return null;
  }
  return Office.context.roamingSettings;
};

export const saveRoamingSettingsAsync = (): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(result.error.message));
        return;
      }
      resolve();
    });
  });
};

export const getEmailSyncOnSend = (): boolean | undefined => {
  const settings = getRoamingSettings();
  if (!settings) {
    return undefined;
  }
  return settings.get(LOXO_ROAMING_KEYS.emailSyncOnSend);
};

export const getPodUrl = (): string | undefined => {
  const settings = getRoamingSettings();
  if (!settings) {
    return undefined;
  }
  return settings.get(LOXO_ROAMING_KEYS.podUrl);
};

export const getRefreshToken = (): string | undefined => {
  const settings = getRoamingSettings();
  if (!settings) {
    return undefined;
  }
  const value = settings.get(LOXO_ROAMING_KEYS.refreshToken);
  return value ? String(value) : undefined;
};

export const getJwt = (): string | undefined => {
  const settings = getRoamingSettings();
  if (!settings) {
    return undefined;
  }
  const value = settings.get(LOXO_ROAMING_KEYS.jwt);
  return value ? String(value) : undefined;
};

const setRoamingValue = async (key: string, value: unknown): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    throw new Error('Roaming settings are not available in this environment.');
  }
  const previousValue = settings.get(key);
  settings.set(key, value);
  try {
    await saveRoamingSettingsAsync();
  } catch (error) {
    if (previousValue === undefined) {
      settings.remove(key);
    } else {
      settings.set(key, previousValue);
    }
    throw error;
  }
  notifyRoamingSettingsChanged();
};

const removeRoamingValue = async (key: string): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    throw new Error('Roaming settings are not available in this environment.');
  }
  const previousValue = settings.get(key);
  settings.remove(key);
  try {
    await saveRoamingSettingsAsync();
  } catch (error) {
    if (previousValue !== undefined) {
      settings.set(key, previousValue);
    }
    throw error;
  }
  notifyRoamingSettingsChanged();
};

export const setEmailSyncOnSend = async (value: boolean): Promise<void> => {
  await setRoamingValue(LOXO_ROAMING_KEYS.emailSyncOnSend, value);
};

export const setPodUrl = async (podUrl: string): Promise<void> => {
  await setRoamingValue(LOXO_ROAMING_KEYS.podUrl, podUrl);
};

export const setRefreshToken = async (refreshToken: string): Promise<void> => {
  await setRoamingValue(LOXO_ROAMING_KEYS.refreshToken, refreshToken);
};

export const setJwt = async (jwt: string): Promise<void> => {
  await setRoamingValue(LOXO_ROAMING_KEYS.jwt, jwt);
};

export const clearRefreshToken = async (): Promise<void> => {
  await removeRoamingValue(LOXO_ROAMING_KEYS.refreshToken);
};

export const saveAuthToRoaming = async (params: {
  podUrl: string;
  jwt: string;
  refreshToken: string;
}): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    throw new Error('Roaming settings are not available in this environment.');
  }

  const previousPodUrl = settings.get(LOXO_ROAMING_KEYS.podUrl);
  const previousJwt = settings.get(LOXO_ROAMING_KEYS.jwt);
  const previousRefreshToken = settings.get(LOXO_ROAMING_KEYS.refreshToken);
  settings.set(LOXO_ROAMING_KEYS.podUrl, params.podUrl);
  settings.remove(LOXO_ROAMING_KEYS.jwt);
  settings.set(LOXO_ROAMING_KEYS.refreshToken, params.refreshToken);
  try {
    await saveRoamingSettingsAsync();
  } catch (error) {
    if (previousPodUrl === undefined) {
      settings.remove(LOXO_ROAMING_KEYS.podUrl);
    } else {
      settings.set(LOXO_ROAMING_KEYS.podUrl, previousPodUrl);
    }
    if (previousJwt === undefined) {
      settings.remove(LOXO_ROAMING_KEYS.jwt);
    } else {
      settings.set(LOXO_ROAMING_KEYS.jwt, previousJwt);
    }
    if (previousRefreshToken === undefined) {
      settings.remove(LOXO_ROAMING_KEYS.refreshToken);
    } else {
      settings.set(LOXO_ROAMING_KEYS.refreshToken, previousRefreshToken);
    }
    throw error;
  }
  notifyRoamingSettingsChanged();
};

export const clearJwtAndRefreshTokens = async (): Promise<void> => {
  const settings = getRoamingSettings();
  if (!settings) {
    return;
  }
  const previousJwt = settings.get(LOXO_ROAMING_KEYS.jwt);
  const previousRefreshToken = settings.get(LOXO_ROAMING_KEYS.refreshToken);
  settings.remove(LOXO_ROAMING_KEYS.jwt);
  settings.remove(LOXO_ROAMING_KEYS.refreshToken);
  try {
    await saveRoamingSettingsAsync();
  } catch (error) {
    if (previousJwt !== undefined) {
      settings.set(LOXO_ROAMING_KEYS.jwt, previousJwt);
    }
    if (previousRefreshToken !== undefined) {
      settings.set(LOXO_ROAMING_KEYS.refreshToken, previousRefreshToken);
    }
    throw error;
  }
  notifyRoamingSettingsChanged();
};
